import React, { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { MAIN_API } from "../../api/apilist";
import { beautyListProfessionalsApi, beautyListServicesApi } from "../../api/organizerBeautyApi";
import "../reservas/reservas-organizer.css";

/* ─────────────────────────────────────────
   SalonCard — la card del salón en la página principal del módulo, con el mismo
   markup que ClubCardItem (Canchas): portada, estado, stats y los dos botones.
───────────────────────────────────────── */

const FALLBACK_IMAGE = "https://via.placeholder.com/400x130?text=Salon";

const resolveUrl = (u) => {
  if (!u) return "";
  if (/^(https?:|blob:)/i.test(u)) return u;
  return u.startsWith("/") ? `${MAIN_API}${u}` : `${MAIN_API}/${u}`;
};

const svg = { fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };

const dos = (n) => (typeof n === "number" ? String(n).padStart(2, "0") : "—");

const Stat = ({ icon, valor, label }) => (
  <span className="zr-stat">
    <svg width="20" height="20" viewBox="0 0 24 24" {...svg}>{icon}</svg>
    <span className="zr-stat__txt">
      <b>{valor}</b>
      <span>{label}</span>
    </span>
  </span>
);

const ItemMenu = ({ onClick, children, icon }) => (
  <button type="button" onClick={onClick}>
    <svg width="14" height="14" viewBox="0 0 24 24" {...svg}>{icon}</svg>
    {children}
  </button>
);

/** Cantidades de servicios y profesionales; si alguna falla se muestra "—" y la card sigue andando. */
const useConteos = (salonId) => {
  const [conteos, setConteos] = useState({ servicios: null, profesionales: null });
  useEffect(() => {
    let vivo = true;
    const cuenta = async (llamada) => {
      try {
        const res = await llamada();
        return Array.isArray(res?.data?.info) ? res.data.info.length : null;
      } catch (_) {
        return null;
      }
    };
    (async () => {
      const [servicios, profesionales] = await Promise.all([cuenta(() => beautyListServicesApi(salonId)), cuenta(() => beautyListProfessionalsApi(salonId))]);
      if (vivo) setConteos({ servicios, profesionales });
    })();
    return () => { vivo = false; };
  }, [salonId]);
  return conteos;
};

const SalonCardItem = ({ salon, states, onVerAgenda, onVerClientes, onVerProfesionales, onEditar, onToggleStatus, onCopiarEnlace, onEliminar }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const conteos = useConteos(salon._id);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const cerrar = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", cerrar);
    return () => document.removeEventListener("mousedown", cerrar);
  }, [menuOpen]);

  const provincia = salon.stateid?.name || states.find((s) => String(s._id) === String(salon.stateid?._id || salon.stateid))?.name || "";
  const ubicacion = [salon.address, salon.cityid?.name, provincia].filter(Boolean).join(" • ");
  const activo = salon.isEnabled !== false;
  const portada = salon.image ? resolveUrl(salon.image) : FALLBACK_IMAGE;
  const accion = (fn) => () => { setMenuOpen(false); fn(salon); };

  return (
    <Col xs={12} md={6} xl={4}>
      <div className="zr-barcard" style={menuOpen ? { zIndex: 10 } : {}}>
        <div className="zr-barcard__cover" style={{ backgroundImage: `url(${portada})` }}>
          <span className={`zr-barcard__status ${activo ? "zr-barcard__status--active" : "zr-barcard__status--inactive"}`}>
            <span className="dot" />
            {activo ? "Activo" : "Inactivo"}
          </span>
        </div>
        <div className="zr-barcard__body">
          <div className="zr-barcard__titlerow">
            <h3 className="zr-barcard__name">{salon.name}</h3>
            <div ref={menuRef} style={{ position: "relative" }}>
              <button type="button" className="zr-barcard__menu" onClick={() => setMenuOpen((v) => !v)} aria-label="Acciones">
                •••
              </button>
              {menuOpen && (
                <div className="zr-cardmenu">
                  <ItemMenu onClick={accion(onEditar)} icon={<><path d="M16.5 4.5 19.5 7.5 8 19l-3.5.5L5 16z" /><path d="M14.5 6.5 17.5 9.5" /></>}>Editar</ItemMenu>
                  <ItemMenu onClick={accion(onCopiarEnlace)} icon={<><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></>}>Copiar enlace</ItemMenu>
                  <ItemMenu onClick={accion(onToggleStatus)} icon={<><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></>}>
                    {activo ? "Desactivar" : "Activar"}
                  </ItemMenu>
                  {onEliminar && (
                    <ItemMenu onClick={accion(onEliminar)} icon={<><path d="M3 6h18" /><path d="M8 6V4h8v2" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /></>}>
                      Eliminar
                    </ItemMenu>
                  )}
                </div>
              )}
            </div>
          </div>
          {ubicacion && (
            <p className="zr-barcard__addr">
              <svg width="14" height="17" viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2C8.1 2 5 5.1 5 9c0 5 7 13 7 13s7-8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5z" /></svg>
              {ubicacion}
            </p>
          )}
          <div className="zr-barcard__stats">
            <Stat valor={dos(conteos.servicios)} label="Servicios" icon={<><path d="M6 3v7a3 3 0 0 0 6 0V3" /><path d="M9 13v8" /><path d="M17 3c-2 2-3 4.5-3 7 0 1.5 1 2.5 3 2.5V21" /></>} />
            <Stat valor={dos(conteos.profesionales)} label="Profesionales" icon={<><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c.6-3.4 3-5.5 6.5-5.5s5.9 2.1 6.5 5.5" /><path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" /><path d="M18.5 14.8c1.6.8 2.7 2.4 3 5.2" /></>} />
            <Stat valor={dos(Number.isFinite(salon.upcomingCount) ? salon.upcomingCount : 0)} label="Turnos próximos" icon={<><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>} />
          </div>
          <div className="zr-barcard__footer">
            <button type="button" className="zr-btn zr-btn--lime zr-btn--block" onClick={() => onVerAgenda(salon)}>
              <svg width="14" height="14" viewBox="0 0 24 24" {...svg} strokeWidth={2}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><path d="m9 16 2 2 4-4" /></svg>
              Ver agenda
            </button>
            <button
              type="button"
              className="zr-btn zr-btn--dark zr-btn--block"
              onClick={() => (onVerProfesionales ? onVerProfesionales(salon) : onEditar(salon, "profesionales"))}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" {...svg}>
                <circle cx="9" cy="8" r="3.5" />
                <path d="M2.5 20c.6-3.4 3-5.5 6.5-5.5s5.9 2.1 6.5 5.5" />
                <path d="M16 4.6a3.5 3.5 0 0 1 0 6.8" />
                <path d="M18.5 14.8c1.6.8 2.7 2.4 3 5.2" />
              </svg>
              Ver Profesionales
            </button>
            {onVerClientes && (
              <button type="button" className="zr-btn zr-btn--soft zr-btn--block" onClick={() => onVerClientes(salon)}>
                <svg width="14" height="14" viewBox="0 0 24 24" {...svg}>
                  <path d="M4 4h12a2 2 0 0 1 2 2v14H6a2 2 0 0 1-2-2z" />
                  <path d="M18 8h2v12h-2" />
                  <path d="M8 9h6M8 13h6" />
                </svg>
                Ver Clientes
              </button>
            )}
          </div>
        </div>
      </div>
    </Col>
  );
};

/** Una card por salón: `salones` (negocio multi-sede) o el `salon` único. Las acciones reciben el salón de la card. */
const SalonCard = ({ salon, salones, states, onVerAgenda, onVerClientes, onVerProfesionales, onEditar, onToggleStatus, onCopiarEnlace, onEliminar }) => (
  <Row className="zr-bars-grid">
    {(salones?.length ? salones : [salon]).map((s) => (
      <SalonCardItem
        key={s._id}
        salon={s}
        states={states || []}
        onVerAgenda={() => onVerAgenda?.(s)}
        onVerClientes={() => onVerClientes?.(s)}
        onVerProfesionales={() => onVerProfesionales?.(s)}
        onEditar={() => onEditar?.(s)}
        onToggleStatus={onToggleStatus}
        onCopiarEnlace={onCopiarEnlace}
        onEliminar={onEliminar}
      />
    ))}
  </Row>
);

export default SalonCard;
